// ** Redux Imports
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { Dispatch } from 'redux'

// ** Types
import { RestaurantWithCombinedUsers } from '@/src/services/delegationService'

// ** Action Types
const FETCH_DELEGATION_DATA = 'delegation/fetchDelegationData'
const FETCH_DELEGATED_ONLY_RESTAURANTS = 'delegation/fetchDelegatedOnlyRestaurants'
const FETCH_DELEGATED_RESTAURANTS_LISTED = 'delegation/fetchDelegatedRestaurantsListed'
const FETCH_DELEGATED_ONLY_META = 'delegation/fetchDelegatedOnlyMeta'

// ** Redux Interface
interface Redux {
  getState: any
  dispatch: Dispatch<any>
}

// ** Initial State Interface
export type DelegatedOnlyMetaDto = {
  totalCount: number
  hasAny: boolean
}

interface DelegationInitialState {
  delegationData: RestaurantWithCombinedUsers[]
  delegatedOnlyRestaurants: RestaurantWithCombinedUsers[]
  delegatedRestaurantsListed: RestaurantWithCombinedUsers[]
  delegatedRestaurantsMeta: any
  delegatedOnlyMeta: DelegatedOnlyMetaDto | null
  loading: boolean
  delegatedOnlyLoading: boolean
  delegatedRestaurantsListedLoading: boolean
  delegatedOnlyMetaLoading: boolean
  error: string | null
  success: string | null
}

// ** Initial State
const initialState: DelegationInitialState = {
  delegationData: [],
  delegatedOnlyRestaurants: [],
  delegatedRestaurantsListed: [],
  delegatedRestaurantsMeta: null,
  delegatedOnlyMeta: null,
  loading: false,
  delegatedOnlyLoading: false,
  delegatedRestaurantsListedLoading: false,
  delegatedOnlyMetaLoading: false,
  error: null,
  success: null
}

// ** Async Thunk for fetching delegation data
export const fetchDelegationData = createAsyncThunk<RestaurantWithCombinedUsers[], { countryId: number }, Redux>(
  FETCH_DELEGATION_DATA,
  async ({ countryId }, { extra, rejectWithValue }) => {
    try {
      const response = await (extra as any).httpClient.get('/restaurants/delegation/with-users-and-franchise', {
        params: {
          page: 1,
          limit: 10000,
          countryId
        }
      })

      // Handle different response structures
      const delegationResponse = response.data
      const delegationData = delegationResponse.data || delegationResponse || []

      return delegationData
    } catch (error: any) {
      console.warn('Failed to fetch delegation data:', error)

      return rejectWithValue(error.response?.data || error.message || 'Failed to fetch delegation data')
    }
  }
)

// ** Async Thunk for fetching delegated-only restaurants
export const fetchDelegatedOnlyRestaurants = createAsyncThunk<
  RestaurantWithCombinedUsers[],
  { countryId: number; page?: number; limit?: number; isActive?: boolean; userId?: number },
  Redux
>(
  FETCH_DELEGATED_ONLY_RESTAURANTS,
  async ({ countryId, page = 1, limit = 10000, isActive = true, userId }, { extra, rejectWithValue }) => {
    try {
      const params: any = {
        page,
        limit,
        isActive,
        countryId
      }

      // Add userId parameter if provided
      if (userId) {
        params.userId = userId
      }

      const response = await (extra as any).httpClient.get('/restaurants/delegated-only', {
        params
      })

      // Handle different response structures
      const delegatedResponse = response.data
      const delegatedData = delegatedResponse.data || delegatedResponse || []

      return delegatedData
    } catch (error: any) {
      console.warn('Failed to fetch delegated-only restaurants:', error)

      return rejectWithValue(error.response?.data || error.message || 'Failed to fetch delegated-only restaurants')
    }
  }
)

// ** Async Thunk for fetching delegated restaurants with pagination (like gyg365RestroListed)
export const fetchDelegatedRestaurantsListed = createAsyncThunk<
  { data: RestaurantWithCombinedUsers[]; meta: any },
  any,
  Redux
>(FETCH_DELEGATED_RESTAURANTS_LISTED, async (params, { extra, rejectWithValue }) => {
  try {
    // Transform params to match what the backend expects
    const transformedParams: any = {
      page: (params.page || 0) + 1, // Convert 0-based to 1-based
      limit: params.pageSize || 10,
      sortBy: params.sortBy,
      filters: params.filters,
      countryId: params.countryId,
      isActive: true
    }

    // Add userId parameter if provided
    if (params.userId) {
      transformedParams.userId = params.userId
    }

    const response = await (extra as any).httpClient.get('/restaurants/delegated-only', {
      params: transformedParams
    })

    // Handle different response structures
    const responseData = response.data
    const data = responseData.data || responseData || []
    const meta = responseData.meta || { totalRows: data.length, pages: 1 }

    return {
      data,
      meta
    }
  } catch (error: any) {
    console.warn('Failed to fetch delegated restaurants listed:', error)

    return rejectWithValue(error.response?.data || error.message || 'Failed to fetch delegated restaurants listed')
  }
})

export const fetchDelegatedOnlyMeta = createAsyncThunk<
  DelegatedOnlyMetaDto,
  { countryId: number; userId?: number },
  Redux
>(FETCH_DELEGATED_ONLY_META, async ({ countryId, userId }, { extra, rejectWithValue }) => {
  try {
    const params: Record<string, number> = { countryId }
    if (userId) {
      params.userId = userId
    }

    const response = await (extra as any).httpClient.get('/restaurants/delegated-only/meta', {
      params
    })

    const body = response.data?.data ?? response.data
    const totalCount = typeof body?.totalCount === 'number' ? body.totalCount : 0
    const hasAny = Boolean(body?.hasAny ?? totalCount > 0)

    return { totalCount, hasAny }
  } catch (error: any) {
    console.warn('Failed to fetch delegated-only meta:', error)

    return rejectWithValue(error.response?.data || error.message || 'Failed to fetch delegated-only meta')
  }
})

// ** Delegation Slice
const delegationSlice = createSlice({
  name: 'delegation',
  initialState,
  reducers: {
    clearDelegationData: state => {
      state.delegationData = []
      state.delegatedOnlyRestaurants = []
      state.delegatedRestaurantsListed = []
      state.delegatedRestaurantsMeta = null
      state.delegatedOnlyMeta = null
      state.loading = false
      state.delegatedOnlyLoading = false
      state.delegatedRestaurantsListedLoading = false
      state.delegatedOnlyMetaLoading = false
      state.error = null
      state.success = null
    },
    clearDelegationError: state => {
      state.error = null
      state.success = null
    }
  },
  extraReducers: builder => {
    builder

      // Fetch delegation data
      .addCase(fetchDelegationData.pending, state => {
        state.loading = true
        state.error = null
        state.success = null
      })
      .addCase(fetchDelegationData.fulfilled, (state, action) => {
        state.loading = false
        state.delegationData = action.payload
        state.error = null
        state.success = 'Delegation data fetched successfully'
      })
      .addCase(fetchDelegationData.rejected, (state, action) => {
        state.loading = false
        state.delegationData = []
        state.error = (action.payload as string) || 'Failed to fetch delegation data'
        state.success = null
      })

      // Fetch delegated-only restaurants
      .addCase(fetchDelegatedOnlyRestaurants.pending, state => {
        state.delegatedOnlyLoading = true
        state.error = null
        state.success = null
      })
      .addCase(fetchDelegatedOnlyRestaurants.fulfilled, (state, action) => {
        state.delegatedOnlyLoading = false
        state.delegatedOnlyRestaurants = action.payload
        state.error = null
        state.success = 'Delegated-only restaurants fetched successfully'
      })
      .addCase(fetchDelegatedOnlyRestaurants.rejected, (state, action) => {
        state.delegatedOnlyLoading = false
        state.delegatedOnlyRestaurants = []
        state.error = (action.payload as string) || 'Failed to fetch delegated-only restaurants'
        state.success = null
      })

      // Fetch delegated restaurants listed (with pagination)
      .addCase(fetchDelegatedRestaurantsListed.pending, state => {
        state.delegatedRestaurantsListedLoading = true
        state.error = null
        state.success = null
      })
      .addCase(fetchDelegatedRestaurantsListed.fulfilled, (state, action) => {
        state.delegatedRestaurantsListedLoading = false
        state.delegatedRestaurantsListed = action.payload.data
        state.delegatedRestaurantsMeta = action.payload.meta
        state.error = null
        state.success = 'Delegated restaurants listed fetched successfully'
      })
      .addCase(fetchDelegatedRestaurantsListed.rejected, (state, action) => {
        state.delegatedRestaurantsListedLoading = false
        state.delegatedRestaurantsListed = []
        state.delegatedRestaurantsMeta = null
        state.error = (action.payload as string) || 'Failed to fetch delegated restaurants listed'
        state.success = null
      })

      .addCase(fetchDelegatedOnlyMeta.pending, state => {
        state.delegatedOnlyMetaLoading = true
        state.error = null
      })
      .addCase(fetchDelegatedOnlyMeta.fulfilled, (state, action) => {
        state.delegatedOnlyMetaLoading = false
        state.delegatedOnlyMeta = action.payload
        state.error = null
      })
      .addCase(fetchDelegatedOnlyMeta.rejected, (state, action) => {
        state.delegatedOnlyMetaLoading = false
        state.delegatedOnlyMeta = null
        state.error = (action.payload as string) || 'Failed to fetch delegated-only meta'
      })
  }
})

// ** Export actions
export const { clearDelegationData, clearDelegationError } = delegationSlice.actions

// ** Export reducer
export default delegationSlice.reducer
